/**
 * Timezone-aware date utilities.
 *
 * All functions use `Intl.DateTimeFormat` — no external dependencies.
 * The user's IANA timezone (e.g. "America/New_York") is stored in their
 * profile and threaded through the app.
 */

const DEFAULT_TIMEZONE = 'America/New_York';

function tz(timezone?: string | null): string {
  return timezone ?? DEFAULT_TIMEZONE;
}

/**
 * The IANA zone every helper here falls back to: the profile's, else
 * `America/New_York`. For a caller that must hand a concrete zone on.
 */
export function resolveTimezone(timezone?: string | null): string {
  return tz(timezone);
}

// ---------------------------------------------------------------------------
// getTimezoneOffset
// ---------------------------------------------------------------------------

/**
 * Return the UTC offset string (e.g. "-04:00") for an IANA timezone
 * at a specific moment (defaults to now).
 */
export function getTimezoneOffset(
  timezone: string,
  refDate: Date = new Date(),
): string {
  // Format the date in the target timezone to extract the individual parts
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(refDate);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)!.value;

  // Build a "wall-clock" date string in the target timezone
  const wallYear = Number(get('year'));
  const wallMonth = Number(get('month')) - 1;
  const wallDay = Number(get('day'));
  let wallHour = Number(get('hour'));
  // hour12:false can produce "24" for midnight in some locales/engines
  if (wallHour === 24) wallHour = 0;
  const wallMinute = Number(get('minute'));
  const wallSecond = Number(get('second'));

  // Create a UTC date with the wall-clock values
  const wallAsUtc = Date.UTC(
    wallYear,
    wallMonth,
    wallDay,
    wallHour,
    wallMinute,
    wallSecond,
  );

  // The offset is (wall-clock-as-UTC) - (actual UTC millis)
  const diffMs = wallAsUtc - refDate.getTime();
  const totalMinutes = Math.round(diffMs / 60_000);

  const sign = totalMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(totalMinutes);
  const h = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const m = String(absMinutes % 60).padStart(2, '0');

  return `${sign}${h}:${m}`;
}

// ---------------------------------------------------------------------------
// getTodayKey
// ---------------------------------------------------------------------------

/**
 * Return today's YYYY-MM-DD in the user's timezone (NOT UTC).
 * Uses the `en-CA` locale which natively formats as YYYY-MM-DD.
 */
export function getTodayKey(timezone?: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz(timezone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

// ---------------------------------------------------------------------------
// msUntilNextMidnight
// ---------------------------------------------------------------------------

/**
 * Milliseconds from now until the next midnight in the user's timezone.
 * Clamped to ≥ 1s so a caller arming a day-boundary timer can't spin on a
 * sub-second remainder right at the boundary.
 */
export function msUntilNextMidnight(timezone?: string | null): number {
  const todayKey = getTodayKey(timezone);
  const [y, m, d] = todayKey.split('-').map(Number);
  // Anchor at UTC noon so +24h lands on the next calendar day even across
  // DST transitions (a 23/25-hour day can't skip or repeat the date).
  const next = new Date(Date.UTC(y, m - 1, d, 12) + 24 * 60 * 60 * 1000);
  const nextKey = next.toISOString().slice(0, 10);
  const { start } = dateRangeForDay(nextKey, timezone);
  return Math.max(1000, new Date(start).getTime() - Date.now());
}

// ---------------------------------------------------------------------------
// toDateKeyTz
// ---------------------------------------------------------------------------

/**
 * Extract YYYY-MM-DD from an ISO timestamp, interpreted in the user's
 * timezone. Date-only strings (no "T") are returned as-is.
 * Null / undefined / empty string returns ''.
 */
export function toDateKeyTz(
  isoString: string | null | undefined,
  timezone?: string | null,
): string {
  if (!isoString) return '';

  // Date-only string: no time component → return as-is
  if (!isoString.includes('T')) return isoString;

  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz(timezone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

// ---------------------------------------------------------------------------
// toTimeKeyTz
// ---------------------------------------------------------------------------

/**
 * Extract HH:mm from an ISO timestamp, interpreted in the user's timezone.
 * Date-only strings (no "T") return ''. Null / undefined / empty return ''.
 *
 * Counterpart to `toDateKeyTz`. Use when populating a <TimePicker> from a
 * `timestamptz` field so the displayed time matches the time the user
 * originally saved, regardless of whether PostgREST returned the value
 * UTC-normalized or with an offset.
 */
export function toTimeKeyTz(
  isoString: string | null | undefined,
  timezone?: string | null,
): string {
  if (!isoString) return '';
  if (!isoString.includes('T')) return '';

  const date = new Date(isoString);
  // en-GB gives HH:mm in 24-hour format.
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz(timezone),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

// ---------------------------------------------------------------------------
// toIsoWithTz
// ---------------------------------------------------------------------------

/**
 * Append timezone offset to a naive datetime from form input.
 *
 * - Date-only input (no "T") → noon on that day (avoids day-boundary shift).
 * - Input that already has an offset (+ / - / Z after the time) → returned unchanged.
 */
export function toIsoWithTz(
  naiveDatetime: string,
  timezone?: string | null,
): string {
  // Already has an offset — leave it alone
  if (/[Zz]$/.test(naiveDatetime) || /[+-]\d{2}:\d{2}$/.test(naiveDatetime)) {
    return naiveDatetime;
  }

  const resolvedTz = tz(timezone);

  // Date-only → noon
  if (!naiveDatetime.includes('T')) {
    const offset = getTimezoneOffset(
      resolvedTz,
      buildRefDate(naiveDatetime, '12:00:00', resolvedTz),
    );
    return `${naiveDatetime}T12:00:00${offset}`;
  }

  // Datetime without offset
  const [datePart, timePart] = naiveDatetime.split('T');
  const normalizedTime = normalizeTime(timePart);
  const offset = getTimezoneOffset(
    resolvedTz,
    buildRefDate(datePart, normalizedTime, resolvedTz),
  );
  return `${datePart}T${normalizedTime}${offset}`;
}

// ---------------------------------------------------------------------------
// dateRangeForDay
// ---------------------------------------------------------------------------

/**
 * Start (00:00:00) and end (23:59:59) of a calendar day as ISO strings
 * with the timezone offset, suitable for Supabase/Postgres range queries.
 */
export function dateRangeForDay(
  dateKey: string,
  timezone?: string | null,
): { start: string; end: string } {
  const resolvedTz = tz(timezone);
  const startOffset = getTimezoneOffset(
    resolvedTz,
    buildRefDate(dateKey, '00:00:00', resolvedTz),
  );
  const endOffset = getTimezoneOffset(
    resolvedTz,
    buildRefDate(dateKey, '23:59:59', resolvedTz),
  );

  return {
    start: `${dateKey}T00:00:00${startOffset}`,
    end: `${dateKey}T23:59:59${endOffset}`,
  };
}

// ---------------------------------------------------------------------------
// dateRangeForMonth
// ---------------------------------------------------------------------------

/**
 * Start of the first day and end of the last day of a month, with offsets.
 */
export function dateRangeForMonth(
  year: number,
  month: number,
  timezone?: string | null,
): { start: string; end: string } {
  const resolvedTz = tz(timezone);

  const startKey = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate(); // month is 1-based here; Date(year, month, 0) gives last day of previous 0-indexed month which equals last day of our month
  const endKey = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const startOffset = getTimezoneOffset(
    resolvedTz,
    buildRefDate(startKey, '00:00:00', resolvedTz),
  );
  const endOffset = getTimezoneOffset(
    resolvedTz,
    buildRefDate(endKey, '23:59:59', resolvedTz),
  );

  return {
    start: `${startKey}T00:00:00${startOffset}`,
    end: `${endKey}T23:59:59${endOffset}`,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Normalize a time string to HH:MM:SS (add :00 seconds if missing).
 */
function normalizeTime(time: string): string {
  // "14:00" → "14:00:00"
  const parts = time.split(':');
  while (parts.length < 3) parts.push('00');
  return parts.join(':');
}

/**
 * Build a reference Date object from a date key and time string, treating
 * them as if they were in the given timezone. This is used to determine the
 * correct offset for that wall-clock moment (important around DST boundaries).
 *
 * We approximate by constructing UTC and adjusting — this is only used to
 * pick the right offset, so minor inaccuracy around the DST transition
 * second is acceptable.
 */
function buildRefDate(dateKey: string, time: string, timezone: string): Date {
  // Start with a rough guess: interpret as UTC
  const rough = new Date(`${dateKey}T${time}Z`);

  // Get the offset at that rough moment
  const roughOffset = getTimezoneOffsetMinutes(timezone, rough);

  // Adjust: the actual UTC moment for the given wall-clock time is
  // wall-clock minus offset
  return new Date(rough.getTime() - roughOffset * 60_000);
}

/**
 * Internal version that returns offset in minutes (positive = east of UTC).
 */
function getTimezoneOffsetMinutes(timezone: string, refDate: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(refDate);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)!.value;

  let hour = Number(get('hour'));
  if (hour === 24) hour = 0;

  const wallAsUtc = Date.UTC(
    Number(get('year')),
    Number(get('month')) - 1,
    Number(get('day')),
    hour,
    Number(get('minute')),
    Number(get('second')),
  );

  return Math.round((wallAsUtc - refDate.getTime()) / 60_000);
}
